import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Switch,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import {
  Accessibility as AccessibilityIcon,
  TextIncrease as FontSizeIcon,
  Contrast as ContrastIcon,
  VolumeUp as SpeechIcon,
  KeyboardArrowUp as FocusIcon,
} from '@mui/icons-material';

interface AccessibilitySettings {
  fontSize: number; // 100 = default, 150 = 150%
  contrast: 'normal' | 'high' | 'dark';
  reduceMotion: boolean;
  screenReader: boolean;
  keyboardNavigation: boolean;
  focusIndicators: boolean;
  speechRate: number; // 0.5 to 2
  voiceEnabled: boolean;
}

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSetting: <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => void;
  resetSettings: () => void;
  speak: (text: string) => void;
  announceToScreenReader: (message: string) => void;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 100,
  contrast: 'normal', // Always normal (white mode)
  reduceMotion: false,
  screenReader: false,
  keyboardNavigation: true,
  focusIndicators: false,
  speechRate: 1,
  voiceEnabled: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(defaultSettings);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Load settings from localStorage
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        const parsedSettings = JSON.parse(saved);
        setSettings({ ...defaultSettings, ...parsedSettings });
      } catch (error) {
        console.warn('Failed to load accessibility settings:', error);
      }
    }

    // Detect system preferences
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setSettings(prev => ({ ...prev, reduceMotion: true }));
    }

    // Always use normal (white) mode, ignore system preferences
    // Force normal contrast regardless of system settings
    setSettings(prev => ({ ...prev, contrast: 'normal' }));

    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Save settings to localStorage
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));

    // Apply CSS custom properties
    const root = document.documentElement;
    
    // Font size
    root.style.setProperty('--accessibility-font-scale', `${settings.fontSize / 100}`);
    
    // Contrast
    if (settings.contrast === 'high') {
      root.classList.add('high-contrast');
      root.classList.remove('dark-mode');
    } else if (settings.contrast === 'dark') {
      root.classList.add('dark-mode');
      root.classList.remove('high-contrast');
    } else {
      root.classList.remove('high-contrast', 'dark-mode');
    }

    // Reduced motion
    if (settings.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Focus indicators
    if (settings.focusIndicators) {
      root.classList.add('enhanced-focus');
    } else {
      root.classList.remove('enhanced-focus');
    }

  }, [settings, mounted]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
  };

  const speak = (text: string) => {
    if (!settings.voiceEnabled || !('speechSynthesis' in window)) return;

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'cs-CZ';
    utterance.rate = settings.speechRate;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    speechSynthesis.speak(utterance);
  };

  const announceToScreenReader = (message: string) => {
    // Create a live region for screen reader announcements
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';

    document.body.appendChild(announcement);
    announcement.textContent = message;

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  const contextValue: AccessibilityContextType = {
    settings,
    updateSetting,
    resetSettings,
    speak,
    announceToScreenReader,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}
      
      {/* Accessibility Settings FAB - Removed */}

      {/* Accessibility Settings Dialog - Hidden */}
      {false && <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="accessibility-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle id="accessibility-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessibilityIcon sx={{ mr: 1 }} />
            <Typography variant="h6" component="h2">
              Nastavení přístupnosti
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Přizpůsobte si zobrazení a ovládání podle vašich potřeb
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {/* Font Size */}
          <Box sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <FontSizeIcon sx={{ mr: 1 }} />
              Velikost písma: {settings.fontSize}%
            </FormLabel>
            <Slider
              value={settings.fontSize}
              onChange={(_, value) => updateSetting('fontSize', value as number)}
              min={80}
              max={200}
              step={10}
              marks={[
                { value: 80, label: '80%' },
                { value: 100, label: '100%' },
                { value: 150, label: '150%' },
                { value: 200, label: '200%' },
              ]}
              aria-label="Velikost písma"
              sx={{
                '& .MuiSlider-thumb': {
                  '&:focus': {
                    outline: 'none',
                  },
                },
              }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Contrast */}
          <FormControl component="fieldset" sx={{ mb: 4, width: '100%' }}>
            <FormLabel component="legend" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <ContrastIcon sx={{ mr: 1 }} />
              Kontrast a barevné schéma
            </FormLabel>
            <RadioGroup
              value={settings.contrast}
              onChange={(e) => updateSetting('contrast', e.target.value as any)}
              aria-label="Nastavení kontrastu"
            >
              <FormControlLabel
                value="normal"
                control={<Radio />}
                label="Normální kontrast"
              />
              <FormControlLabel
                value="high"
                control={<Radio />}
                label="Vysoký kontrast"
              />
              <FormControlLabel
                value="dark"
                control={<Radio />}
                label="Tmavý režim"
              />
            </RadioGroup>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          {/* Voice Settings */}
          <Box sx={{ mb: 4 }}>
            <FormLabel component="legend" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <SpeechIcon sx={{ mr: 1 }} />
              Hlasové čtení
            </FormLabel>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Povolit hlasové čtení
              </Typography>
              <Switch
                checked={settings.voiceEnabled}
                onChange={(e) => updateSetting('voiceEnabled', e.target.checked)}
                aria-label="Povolit hlasové čtení"
              />
            </Box>

            {settings.voiceEnabled && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Rychlost řeči: {settings.speechRate}x
                </Typography>
                <Slider
                  value={settings.speechRate}
                  onChange={(_, value) => updateSetting('speechRate', value as number)}
                  min={0.5}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: 0.5, label: '0.5x' },
                    { value: 1, label: '1x' },
                    { value: 1.5, label: '1.5x' },
                    { value: 2, label: '2x' },
                  ]}
                  aria-label="Rychlost řeči"
                />
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Other Settings */}
          <Box sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <FocusIcon sx={{ mr: 1 }} />
              Další nastavení
            </FormLabel>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  Snížit animace a pohyb
                </Typography>
                <Switch
                  checked={settings.reduceMotion}
                  onChange={(e) => updateSetting('reduceMotion', e.target.checked)}
                  aria-label="Snížit animace"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  Zdůrazněné indikátory fokusu
                </Typography>
                <Switch
                  checked={settings.focusIndicators}
                  onChange={(e) => updateSetting('focusIndicators', e.target.checked)}
                  aria-label="Zdůrazněné indikátory fokusu"
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2">
                  Podpora čtečky obrazovky
                </Typography>
                <Switch
                  checked={settings.screenReader}
                  onChange={(e) => updateSetting('screenReader', e.target.checked)}
                  aria-label="Podpora čtečky obrazovky"
                />
              </Box>
            </Box>
          </Box>

          {/* Accessibility Info */}
          <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
            <Typography variant="body2">
              <strong>Tip:</strong> Můžete použít klávesové zkratky:
              <br />• Tab - navigace mezi prvky
              <br />• Enter/Space - aktivace tlačítek
              <br />• Esc - zavření dialogů
              <br />• Alt + A - otevření tohoto menu
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={resetSettings}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Obnovit výchozí
          </Button>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            Hotovo
          </Button>
        </DialogActions>
      </Dialog>}

      {/* Global Styles */}
      <style>{`
        /* Accessibility CSS */
        .high-contrast {
          filter: contrast(150%) brightness(110%);
        }
        
        /* Dark mode removed - always white mode */
        
        .reduce-motion * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
        
        /* Accessible focus ring (WCAG 2.2 AA) */
        .enhanced-focus *:focus-visible {
          outline: 3px solid var(--focus-ring-color, #1F6FEB) !important;
          outline-offset: 2px;
          box-shadow: 0 0 0 2px rgba(255,255,255,0.9);
          border-radius: 6px;
        }
        /* Ensure keyboard-only users get visible focus even without :focus-visible support */
        .enhanced-focus *:focus {
          outline-color: var(--focus-ring-color, #1F6FEB);
        }
        
        /* Font scaling */
        html {
          font-size: calc(16px * var(--accessibility-font-scale, 1));
        }
        
        /* Skip to content link */
        .skip-link {
          position: absolute;
          top: -40px;
          left: 6px;
          background: #000;
          color: #fff;
          padding: 8px;
          text-decoration: none;
          border-radius: 0 0 4px 4px;
          z-index: 9999;
        }
        
        .skip-link:focus {
          top: 0;
        }
        
        /* Screen reader only text */
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
        
        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .MuiButton-root {
            border: 2px solid currentColor !important;
          }
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
    </AccessibilityContext.Provider>
  );
};
